import * as React from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { isTypeRelativeWithStartOnly, isTypeRelativeWithEnd, isNoTimeRangeOverride } from "views/typeGuards/timeRange";
import { readableRange } from "views/logic/queries/TimeRangeToString";
import assertUnreachable from "logic/assertUnreachable";
export const EMPTY_RANGE = "----/--/-- --:--:--.---";
export const EMPTY_OUTPUT = { from: EMPTY_RANGE, until: EMPTY_RANGE };
const TimeRangeWrapper = styled.div(
  ({ theme, $centerTimestamps }) => css`
    width: 100%;
    background-color: ${theme.colors.table.row.backgroundStriped};
    align-items: center;

    ${$centerTimestamps ? css`
          padding: 3px 0;
          display: grid;
          grid-template-columns: 4fr 0.75fr 4fr;
        ` : css`
          padding: 3px 13px;
          display: flex;
          justify-content: space-around;
        `}
  `
);
const TimeRangeCell = styled.span(
  ({ $centerTimestamps, $gridColumn, $fullWidth }) => css`
    ${$centerTimestamps ? css`
          min-width: 0;
          text-align: center;
          grid-column: ${$fullWidth ? "1 / -1" : $gridColumn};
        ` : css`
          flex: 1;
        `}
  `
);
const TimeRangeGap = styled.span(
  ({ $centerTimestamps }) => $centerTimestamps ? css`
        grid-column: 2;
      ` : css`
        display: none;
      `
);
export const range = (timerange) => {
  let from = EMPTY_RANGE;
  let to = EMPTY_RANGE;
  if (!(timerange == null ? void 0 : timerange.type)) {
    return EMPTY_OUTPUT;
  }
  const { type } = timerange;
  switch (type) {
    case "relative":
      if (isTypeRelativeWithStartOnly(timerange)) {
        from = readableRange(timerange, "range");
      }
      if (isTypeRelativeWithEnd(timerange)) {
        from = readableRange(timerange, "from");
      }
      to = readableRange(timerange, "to");
      return {
        from,
        until: to
      };
    case "absolute":
      return { from: timerange.from, until: timerange.to };
    default:
      return assertUnreachable(type, "Invalid time range type");
  }
};
const TimeRange = ({
  centerTimestamps,
  timerange
}) => {
  const { t } = useTranslation("search");
  if ((timerange == null ? void 0 : timerange.type) === "keyword") {
    return /* @__PURE__ */ React.createElement(TimeRangeCell, { $centerTimestamps: centerTimestamps, $fullWidth: centerTimestamps }, t("time_range.keyword_label"), " ", /* @__PURE__ */ React.createElement("b", null, timerange.keyword));
  }
  const { from, until } = range(timerange);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(TimeRangeCell, { "data-testid": "from", $centerTimestamps: centerTimestamps, $gridColumn: "1" }, t("time_range.from_label"), " ", /* @__PURE__ */ React.createElement("b", null, from)), /* @__PURE__ */ React.createElement(TimeRangeGap, { "aria-hidden": true, $centerTimestamps: centerTimestamps }), /* @__PURE__ */ React.createElement(TimeRangeCell, { "data-testid": "to", $centerTimestamps: centerTimestamps, $gridColumn: "3" }, t("time_range.until_label"), " ", /* @__PURE__ */ React.createElement("b", null, until)));
};
const TimeRangeDisplay = ({ timerange, toggleDropdownShow = void 0, centerTimestamps = false }) => {
  const { t } = useTranslation("search");
  return /* @__PURE__ */ React.createElement(
    TimeRangeWrapper,
    {
      $centerTimestamps: centerTimestamps,
      "aria-label": t("time_range.display_label"),
      role: "button",
      onClick: toggleDropdownShow
    },
    isNoTimeRangeOverride(timerange) ? /* @__PURE__ */ React.createElement(TimeRangeCell, { $centerTimestamps: centerTimestamps, $fullWidth: centerTimestamps }, t("time_range.no_override")) : /* @__PURE__ */ React.createElement(TimeRange, { centerTimestamps, timerange })
  );
};
export default TimeRangeDisplay;
